import React, { useMemo, useState, useRef, useEffect } from 'react'
import type { Flashcard } from '../../../../shared/types'
import api from '../../services/api'

interface FlashcardCardProps {
  flashcard: Flashcard
  onDelete?: () => void
  showActions?: boolean
  usePremiumVoices?: boolean
  preferredVoiceId?: string | null
}

export function FlashcardCard({
  flashcard,
  onDelete,
  showActions = true,
  usePremiumVoices = false,
  preferredVoiceId = null,
}: FlashcardCardProps) {
  const [isFlipped, setIsFlipped] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isPlayingAudio, setIsPlayingAudio] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const japaneseAudioCacheRef = useRef<Record<string, string>>({})
  const safeTargetLanguage = flashcard.targetLanguage || 'ja'

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause()
        } catch {}
        audioRef.current = null
      }
    }
  }, [])

  const handlePlayAudio = async () => {
    const targetText = flashcard.targetText || flashcard.japaneseText
    if (isPlayingAudio || (!flashcard.audioUrl && !targetText)) return

    setIsPlayingAudio(true)

    try {
      if (audioRef.current) {
        try {
          audioRef.current.pause()
        } catch {}
        audioRef.current = null
      }

      let clipUrl: string | null = null

      if (usePremiumVoices && targetText) {
        const cacheKey = `${flashcard.id ?? targetText}::${preferredVoiceId || 'default'}`
        let cached = japaneseAudioCacheRef.current[cacheKey]

        if (!cached) {
          try {
            const response = await api.speakJapaneseText(
              targetText,
              'elevenlabs',
              preferredVoiceId || undefined,
              flashcard.targetLanguage || undefined
            )
            cached = response?.audioUrl || ''
            if (cached) {
              japaneseAudioCacheRef.current[cacheKey] = cached
            }
          } catch (err) {
            console.error('[FlashcardCard] Failed to fetch ElevenLabs audio, falling back:', err)
          }
        }

        clipUrl = cached || null
      }

      if (!clipUrl && flashcard.audioUrl) {
        clipUrl = flashcard.audioUrl
      }

      if (!clipUrl) {
        throw new Error('No audio available')
      }

      const audio = new Audio(clipUrl)
      audioRef.current = audio
      audio.onended = () => {
        setIsPlayingAudio(false)
        audioRef.current = null
      }
      audio.onerror = () => {
        setIsPlayingAudio(false)
        audioRef.current = null
      }
      await audio.play()
    } catch (err) {
      console.error('Failed to play audio:', err)
      setIsPlayingAudio(false)
    }
  }

  const getMasteryColor = (level: string) => {
    switch (level) {
      case 'new':
        return 'bg-blue-100 text-blue-700'
      case 'learning':
        return 'bg-yellow-100 text-yellow-700'
      case 'review':
        return 'bg-orange-100 text-orange-700'
      case 'mastered':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const parsedNextReviewDate = useMemo(() => {
    const value = flashcard.nextReviewDate
    if (!value) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number)
      return new Date(year, month - 1, day)
    }
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }, [flashcard.nextReviewDate])

  const nextReviewLabel = useMemo(() => {
    const value = flashcard.nextReviewDate
    if (!value) return 'Next review: -'
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-')
      return `Next review: ${m}/${d}/${y}`
    }
    try {
      return `Next review: ${new Date(value).toLocaleDateString()}`
    } catch {
      return `Next review: ${value}`
    }
  }, [flashcard.nextReviewDate])

  const phoneticHint = useMemo(() => {
    const reading = flashcard.targetReading || flashcard.furigana || ''
    if (!reading) return ''
    return /[A-Za-z]/.test(reading) ? '' : reading
  }, [flashcard.targetReading, flashcard.furigana])

  const isReviewLate = useMemo(() => {
    if (!parsedNextReviewDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return parsedNextReviewDate.getTime() < today.getTime()
  }, [parsedNextReviewDate])

  return (
    <div className="premium-flashcard card-surface rounded-2xl border border-gray-100 shadow-sm transition-all duration-500 hover:shadow-xl">
      {/* Mastery Badge & actions */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getMasteryColor(
            flashcard.masteryLevel
          )}`}
        >
          {flashcard.masteryLevel.charAt(0).toUpperCase() + flashcard.masteryLevel.slice(1)}
        </span>
        <div className="flex items-center gap-2">
          {flashcard.audioUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void handlePlayAudio()
              }}
              disabled={isPlayingAudio}
              className="p-2 rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 transition disabled:opacity-50"
              title="Play pronunciation"
              aria-label="Play pronunciation"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                {isPlayingAudio ? (
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
            </button>
          )}
          {showActions && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                try {
                  // Debug: verify click fires and IDs are present
                  // eslint-disable-next-line no-console
                  console.log('[FlashcardCard] delete clicked', {
                    id: (flashcard as any)?.id,
                    docId: (flashcard as any)?._docId,
                  })
                } catch {}
                onDelete()
              }}
              className="text-gray-400 hover:text-error-600 transition pointer-events-auto cursor-pointer relative z-10"
              title="Delete flashcard"
              aria-label="Delete flashcard"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div
        className="px-6 py-5 cursor-pointer"
        onClick={() => setIsFlipped((prev) => !prev)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`flashcard-3d min-h-[220px] ${isHovered ? 'is-hovered' : ''}`}>
          <div className={`flashcard-3d-inner ${isFlipped ? 'is-flipped' : ''}`}>
            <div className="flashcard-face flashcard-face-front text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2" lang={safeTargetLanguage}>
                {flashcard.targetText || flashcard.japaneseText}
              </div>
              {phoneticHint && (
                <div className="text-sm text-gray-500 mb-4" lang={safeTargetLanguage}>
                  {phoneticHint}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-4">Tap to see meaning</p>
            </div>
            <div className="flashcard-face flashcard-face-back text-center">
              <div className="text-xl font-semibold text-gray-900 mb-4">
                {flashcard.sourceText || flashcard.englishTranslation}
              </div>
              {(flashcard.exampleTarget || flashcard.exampleSentence) && (
                <div className="text-sm text-gray-700 mb-2" lang={safeTargetLanguage}>
                  {flashcard.exampleTarget || flashcard.exampleSentence}
                </div>
              )}
              {(flashcard.exampleSource || flashcard.exampleTranslation) && (
                <div className="text-xs text-gray-500">
                  {flashcard.exampleSource || flashcard.exampleTranslation}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-4">Tap to see target language</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
        <div className="flex justify-between">
          <span className="flex items-center gap-2">
            {nextReviewLabel}
            {isReviewLate && (
              <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold uppercase tracking-wide text-[10px]">
                Late - Review today
              </span>
            )}
          </span>
          <span>
            {(() => {
              const reps = (flashcard as any).totalReviews ?? flashcard.repetitions
              const acc = (flashcard as any).accuracy
              return acc === undefined || acc === null
                ? `Reps: ${reps}`
                : `Reps: ${reps} (${acc}% accuracy)`
            })()}
          </span>
        </div>
      </div>
    </div>
  )
}
